'use client'

import useStoreLoading from '@/store/store-loading'
import { useLenis } from 'lenis/react'
import { usePathname, useSearchParams } from 'next/navigation'
import { useEffect, useLayoutEffect, useRef } from 'react'

type Options = {
  onLoadMore?: () => void
  marginBottomPx?: number
}

const getDefaultMargin = () => {
  if (typeof window === 'undefined') {
    return 650
  }
  const w = window.innerWidth
  if (w < 768) {
    return 1200
  }
  if (w < 1024) {
    return 1050
  }
  return 650
}

const keyFor = (path: string, query: string) => `scroll:${path}?${query}`
const itemsKeyFor = (path: string, query: string) =>
  `scroll-items:${path}?${query}`

const getSaved = (path: string, query: string): number | undefined => {
  if (typeof window === 'undefined') {
    return undefined
  }
  const raw = sessionStorage.getItem(keyFor(path, query))
  if (!raw) {
    return undefined
  }
  const num = Number(raw)
  return Number.isFinite(num) ? num : undefined
}

const getSavedItems = (path: string, query: string): number | undefined => {
  if (typeof window === 'undefined') {
    return undefined
  }
  const raw = sessionStorage.getItem(itemsKeyFor(path, query))
  if (!raw) {
    return undefined
  }
  const num = Number(raw)
  return Number.isFinite(num) ? num : undefined
}

const setSaved = (path: string, query: string, y: number) => {
  if (typeof window === 'undefined') {
    return
  }
  sessionStorage.setItem(keyFor(path, query), String(y))
}

const setSavedItems = (path: string, query: string, items: number) => {
  if (typeof window === 'undefined') {
    return
  }
  sessionStorage.setItem(itemsKeyFor(path, query), String(items))
}

export function useListScrollRestore(options?: Options) {
  const pathname = usePathname()
  const searchParams = useSearchParams()
  const lenis = useLenis((l) => l)
  const setIsLoading = useStoreLoading.use.setIsLoading()
  const ticking = useRef(false)
  const hasRestoredRef = useRef(false)
  const retryCount = useRef(0)
  const imageWaitCount = useRef(0)
  const maxRetries = 12
  const maxImageWaits = 12

  const saveItemsCount = (count: number) => {
    if (!pathname) {
      return
    }
    const query = searchParams?.toString() ?? ''
    setSavedItems(pathname, query, count)
  }

  const getSavedItemsCount = (): number | undefined => {
    if (!pathname) {
      return undefined
    }
    const query = searchParams?.toString() ?? ''
    return getSavedItems(pathname, query)
  }

  useEffect(() => {
    if (!pathname) {
      return
    }
    const query = searchParams?.toString() ?? ''
    const onScroll = () => {
      if (ticking.current) {
        return
      }
      ticking.current = true
      requestAnimationFrame(() => {
        setSaved(pathname, query, window.scrollY)
        ticking.current = false
      })
    }
    window.addEventListener('scroll', onScroll, { passive: true })
    return () => window.removeEventListener('scroll', onScroll)
  }, [pathname, searchParams])

  useEffect(() => {
    if (typeof window === 'undefined') {
      return
    }
    const prev = (history as any).scrollRestoration
    try {
      history.scrollRestoration = 'manual'
    } catch (err) {
      if (process.env.NODE_ENV !== 'production') {
        console.warn(
          '[useListScrollRestore] Failed to set scrollRestoration to manual',
          err
        )
      }
    }
    return () => {
      try {
        history.scrollRestoration = prev || 'auto'
      } catch (err) {
        if (process.env.NODE_ENV !== 'production') {
          console.warn(
            '[useListScrollRestore] Failed to restore scrollRestoration',
            err
          )
        }
      }
    }
  }, [])

  useLayoutEffect(() => {
    if (!pathname) {
      return
    }
    const query = searchParams?.toString() ?? ''
    const saved = getSaved(pathname, query)
    if (saved == null) {
      return
    }
    setIsLoading(true)
    try {
      const doc = document.documentElement
      const maxScrollNow = Math.max(0, doc.scrollHeight - window.innerHeight)
      const conservativeMargin =
        (options?.marginBottomPx ?? getDefaultMargin()) * 2
      const canReachTargetNow = maxScrollNow >= saved - conservativeMargin
      if (canReachTargetNow) {
        const targetNow = Math.min(saved, maxScrollNow)
        if (lenis) {
          lenis?.scrollTo(targetNow, { immediate: true })
        } else {
          window.scrollTo({ top: targetNow, behavior: 'instant' })
        }
      } else {
        const safeOffset = 400
        if (saved > safeOffset && maxScrollNow > safeOffset * 2) {
          const partial = Math.min(
            saved - safeOffset,
            maxScrollNow - safeOffset
          )
          if (partial > 0) {
            if (lenis) {
              lenis?.scrollTo(partial, { immediate: true })
            } else {
              window.scrollTo({ top: partial, behavior: 'instant' })
            }
          }
        }
      }
    } catch (err) {
      if (process.env.NODE_ENV !== 'production') {
        console.warn(
          '[useListScrollRestore] Provisional pre-paint scroll failed',
          err
        )
      }
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [pathname, searchParams, lenis])

  useEffect(() => {
    if (!pathname || hasRestoredRef.current) {
      return
    }
    const query = searchParams?.toString() ?? ''
    const saved = getSaved(pathname, query)
    if (saved == null) {
      return
    }
    const margin = options?.marginBottomPx ?? getDefaultMargin()
    retryCount.current = 0
    imageWaitCount.current = 0

    // Post-settle adjustment helper to counter late content shifts
    const finalAdjustments = (target: number) => {
      let attempt = 0
      const maxAttempts = 3
      const baseDelay = 220
      const tick = () => {
        attempt++
        const finalMaxScroll = Math.max(
          0,
          document.documentElement.scrollHeight - window.innerHeight
        )
        const finalTarget = Math.min(target, finalMaxScroll)
        const dy = Math.abs(finalTarget - window.scrollY)
        if (dy > 40) {
          if (lenis) {
            lenis?.scrollTo(finalTarget, { immediate: true })
          } else {
            window.scrollTo({ top: finalTarget, behavior: 'instant' })
          }
        }
        if (attempt < maxAttempts) {
          setTimeout(tick, baseDelay * (attempt + 1))
        }
      }
      setTimeout(tick, baseDelay)
    }

    const tryRestore = () => {
      if (document.readyState !== 'complete') {
        setTimeout(tryRestore, 40)
        return
      }

      const images = document.querySelectorAll('img')
      const unloadedImages = Array.from(images).filter((img) => !img.complete)
      if (unloadedImages.length > 0 && imageWaitCount.current < maxImageWaits) {
        imageWaitCount.current++
        setTimeout(tryRestore, 60)
        return
      }

      const doc = document.documentElement
      const maxScroll = Math.max(0, doc.scrollHeight - window.innerHeight)

      const conservativeMargin = margin * 2
      const canReachTarget = maxScroll >= saved - conservativeMargin

      if (canReachTarget) {
        if (lenis) {
          lenis?.scrollTo(saved, { immediate: true })
        } else {
          window.scrollTo({ top: saved, behavior: 'instant' })
        }

        setTimeout(() => {
          const finalScrollY = window.scrollY
          const finalMaxScroll = Math.max(
            0,
            document.documentElement.scrollHeight - window.innerHeight
          )

          if (
            Math.abs(saved - finalScrollY) > 50 &&
            finalMaxScroll >= saved - conservativeMargin
          ) {
            if (lenis) {
              lenis?.scrollTo(saved, { immediate: true })
            } else {
              window.scrollTo({ top: saved, behavior: 'instant' })
            }
          }
        }, 120)

        hasRestoredRef.current = true
        setIsLoading(false)
        finalAdjustments(saved)
        return
      }

      if (retryCount.current < maxRetries && options?.onLoadMore) {
        retryCount.current++
        options.onLoadMore()
        setTimeout(tryRestore, 180)
      } else {
        const targetY = Math.min(saved, maxScroll)
        if (lenis) {
          lenis?.scrollTo(targetY, { immediate: true })
        } else {
          window.scrollTo({ top: targetY, behavior: 'instant' })
        }
        hasRestoredRef.current = true
        setIsLoading(false)
        finalAdjustments(saved)
      }
    }

    const t = setTimeout(tryRestore, 80)
    return () => clearTimeout(t)
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [pathname, searchParams, lenis])

  useEffect(() => {
    hasRestoredRef.current = false
    retryCount.current = 0
    imageWaitCount.current = 0
    return () => setIsLoading(false)
  }, [pathname, setIsLoading])

  return {
    saveItemsCount,
    getSavedItemsCount
  }
}
